import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  MenuItem,
  Typography,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import './ArrSelectDropdown.scss';

import ArrTextField from '../arr-text-field/ArrTextField';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import {ReactComponent as SearchIcon} from '../../../../src/assets/images/icon-search.svg';

export interface GroupedItem {
  groupName: string;
  items: {id: string | number; label: string; disabled?: boolean}[];
}

interface ArrGroupedSelectDropdownProps {
  dropdownLabel: string;
  groupedItems: GroupedItem[];
  selectedValue: string | number | null;
  onSelectionChange: (selected: string | number | null) => void;
  searchable?: boolean;
  disabled?: boolean;
  sizeVariant?: 'small' | 'medium' | 'large';
  dataTestId: string;
}

const ArrGroupedSelectDropdown: React.FC<ArrGroupedSelectDropdownProps> = ({
  dropdownLabel,
  groupedItems,
  selectedValue,
  onSelectionChange,
  searchable = true,
  disabled = false,
  sizeVariant = 'medium',
  dataTestId,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchFieldText, setSearchFieldText] = useState('');
  const formControlRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(600);
  const [isTruncatedDisplayText, setIsTruncatedDisplayText] = useState(false);
  const displayTextRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Set dropdown width dynamically
  useEffect(() => {
    if (formControlRef.current)
      setMenuWidth(formControlRef.current.offsetWidth);
  }, [isDropdownOpen]);

  // Dynamic position & height calculation
  useEffect(() => {
    const updateMenuPositionAndHeight = () => {
      if (!formControlRef.current) return;

      const rect = formControlRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide dropdown direction
      const position =
        spaceBelow < 300 && spaceAbove > spaceBelow ? 'top' : 'bottom';
      setMenuPosition(position);

      // Reserve space for fixed elements (search bar)
      const reservedHeight = searchable ? 50 : 0;
      const availableHeight = position === 'bottom' ? spaceBelow : spaceAbove;
      const maxHeight = Math.min(600, availableHeight * 0.8 - reservedHeight);
      setMenuMaxHeight(maxHeight > 0 ? maxHeight : 100); // fallback minimal height
    };

    updateMenuPositionAndHeight();
    window.addEventListener('resize', updateMenuPositionAndHeight);
    window.addEventListener('scroll', updateMenuPositionAndHeight, true);

    if (isDropdownOpen && searchable) {
      // Use a small delay to ensure the menu is fully rendered
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else if (isDropdownOpen) {
      requestAnimationFrame(() => {
        scrollContainerRef.current?.focus();
      });
    }

    return () => {
      window.removeEventListener('resize', updateMenuPositionAndHeight);
      window.removeEventListener('scroll', updateMenuPositionAndHeight, true);
    };
  }, [isDropdownOpen, searchable]);

  // Keep all groups expanded
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const expandedGroups = useMemo(
    () => groupedItems.map((g) => g.groupName),
    [groupedItems]
  );

  // Find selected item
  const selectedItem = useMemo(() => {
    for (const group of groupedItems) {
      const item = group.items.find((item) => item.id === selectedValue);
      if (item) return item;
    }
    return null;
  }, [groupedItems, selectedValue]);

  useEffect(() => {
    const el = displayTextRef.current;
    if (el) {
      setIsTruncatedDisplayText(el.scrollWidth > el.clientWidth);
    }
  }, [selectedValue]);

  const handleItemClick = (id: string | number) => {
    onSelectionChange(id);
    setIsDropdownOpen(false);
    setSearchFieldText('');
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSearchFieldText(e.target.value);
  };

  const handleCloseAndClear = () => {
    setIsDropdownOpen(false);
    setSearchFieldText('');
  };

  const renderSelected = (selectedValue: string | number | null) => {
    if (!selectedValue || !selectedItem) {
      return null;
    }

    return (
      <ArrTooltip
        content={selectedItem.label}
        showOnTruncate
        isTruncated={isTruncatedDisplayText}
      >
        <Typography
          ref={displayTextRef}
          noWrap
          className={`arr-dropdown-selected${disabled ? ' arr-dropdown-selected--disabled' : ''}`}
          data-testid={`arr-grouped-select-dropdown-selected-item-labels-${dataTestId}`}
        >
          {selectedItem.label}
        </Typography>
      </ArrTooltip>
    );
  };

  // Filter groups/items based on search
  const filteredGroups = useMemo(() => {
    if (!searchFieldText.trim()) return groupedItems;
    const query = searchFieldText.toLowerCase();

    return groupedItems
      .map((group) => {
        const groupMatches = group.groupName.toLowerCase().includes(query);

        const matchingItems = group.items.filter((item) =>
          item.label.toLowerCase().includes(query)
        );

        return {
          ...group,
          items: groupMatches ? group.items : matchingItems,
        };
      })
      .filter((group) => group.items.length > 0);
  }, [searchFieldText, groupedItems]);

  return (
    <FormControl
      variant="filled"
      ref={formControlRef}
      className={`arr-dropdown-container arr-dropdown-container--${sizeVariant}`}
      disabled={disabled}
      fullWidth
    >
      {(sizeVariant === 'large' || !selectedItem) && (
        <InputLabel
          id={`${dropdownLabel}-label`}
          className="arr-dropdown-label"
        >
          {dropdownLabel}
        </InputLabel>
      )}
      <Select
        open={isDropdownOpen}
        onOpen={() => setIsDropdownOpen(true)}
        onClose={handleCloseAndClear}
        labelId={`${dropdownLabel}-label`}
        value={selectedValue ?? ''}
        renderValue={(selected) =>
          renderSelected(selected as string | number | null)
        }
        IconComponent={ArrowDropDownIcon}
        disabled={disabled}
        autoWidth={false}
        className={`arr-dropdown-container__arr-dropdown-select arr-dropdown-container__arr-dropdown-select--${sizeVariant}`}
        MenuProps={{
          PaperProps: {
            className: `arr-dropdown-container__arr-dropdown-menu arr-dropdown-container__arr-dropdown-menu--${menuPosition} arr-dropdown-container__arr-dropdown-menu--${sizeVariant}`,
            style: {width: menuWidth, maxWidth: menuWidth},
          },
          anchorOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'bottom', horizontal: 'left'}
              : {vertical: 'top', horizontal: 'left'},
          transformOrigin:
            menuPosition === 'bottom'
              ? {vertical: 'top', horizontal: 'left'}
              : {vertical: 'bottom', horizontal: 'left'},
          MenuListProps: {autoFocusItem: false},
        }}
      >
        {searchable && (
          <Box
            className="arr-dropdown-search"
            data-testid="arr-dropdown-search"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <ArrTextField
              inputRef={searchInputRef}
              fullWidth
              id={`${dropdownLabel}-search`}
              placeholder="Search"
              sizeVariant="medium"
              onChange={handleSearchChange}
              onKeyDown={(e) => e.stopPropagation()}
              value={searchFieldText}
              dataTestId="group-dropdown-search-input"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  role: 'searchbox',
                  'aria-label': `${dropdownLabel} search`,
                  'aria-controls': `${dropdownLabel}-listbox`,
                  'aria-autocomplete': 'list',
                  onClick: (e) => e.stopPropagation(),
                  onMouseDown: (e) => e.stopPropagation(),
                  onMouseUp: (e) => e.stopPropagation(),
                  onFocus: (e) => e.stopPropagation(),
                  endAdornment: searchFieldText.length ? (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<ClearIcon />}
                        onClick={() => setSearchFieldText('')}
                        ariaLabel="clear"
                        shape="none"
                        size="small"
                        data-testid="clear-button"
                        testId="arr-grouped-select-dropdown-clear-icon-button"
                      />
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <ArrIconButton
                        icon={<SearchIcon />}
                        ariaLabel="search"
                        shape="none"
                        size="small"
                        data-testid="search-button"
                        testId="arr-grouped-select-dropdown-search-icon-button"
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        <Box
          ref={scrollContainerRef}
          className="arr-dropdown-scroll arr-grouped-dropdown__scroll-container"
          style={{maxHeight: menuMaxHeight, overflowY: 'auto'}}
        >
          {filteredGroups.map((group) => (
            <Box
              key={group.groupName}
              className="arr-grouped-dropdown__group-container"
            >
              <Typography
                className="arr-dropdown-group-header arr-grouped-dropdown__group-header"
                fontWeight="bold"
                data-testid={`arr-grouped-select-dropdown-typography-text-${dataTestId}s`}
              >
                {group.groupName}
              </Typography>

              {group.items.map((item, index) => (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  onClick={() => handleItemClick(item.id)}
                  disabled={item.disabled}
                  selected={item.id === selectedValue}
                  className="arr-grouped-dropdown__child-item"
                  data-testid={`arr-grouped-select-dropdown-menu-item-${item.id}`}
                >
                  <ArrTooltip content={item.label} showOnTruncate>
                    <Typography
                      className="arr-dropdown-option"
                      data-testid={`arr-grouped-select-dropdown-item-label-${index}-${dataTestId}`}
                    >
                      {item.label}
                    </Typography>
                  </ArrTooltip>
                </MenuItem>
              ))}
            </Box>
          ))}
        </Box>
      </Select>
    </FormControl>
  );
};

export default ArrGroupedSelectDropdown;
